import { useClosedLiftScopes } from '../../api/hooks/useLiftRules';
import { hasNoRoute, noRouteWords } from '../../state/lift-rule-waiting';
import { useRef, useState } from 'react';
import { StatusChip } from '../../design-system/components/StatusChip';
import { SkeletonLoader } from '../../design-system/components/SkeletonLoader';
import { ConfirmDialog } from '../../design-system/components/ConfirmDialog';
import { useTaskLog } from '../../api/hooks/useTaskLog';
import { useDeleteTaskHistory, useRobots } from '../../api/hooks';
import { useTaskDistances } from '../../api/task-distances';
import { useSafeSiteId } from '../../state/useActiveSite';
import { RoleGuard } from '../../auth/RoleGuard';
import { TASK_DELETE_HISTORY_ROLES } from '../../auth/types';
import { DustbagConfirmAction } from '../Fleet/DustbagConfirmAction';
import { isConfirmableDustbagTask } from '../Fleet/dustbag-confirm';
import type { TaskState } from '../../api/rmf-client';
import { TaskTimeline } from './TaskTimeline';
import { TaskControlActions } from './TaskControlActions';
import { TaskPhasesList } from './TaskPhasesList';
import { TaskPlannedList } from './TaskPlannedList';
import { TaskElapsed, TaskRemaining } from './TaskTiming';
import { REMAINING_TOOLTIP, TASK_TIME_TOOLTIP } from './task-timing';
import { TaskDistanceRows } from './TaskDistanceRows';
import { distanceStillGrowing } from './task-distance';
import { TaskQuietBadge } from './TaskQuietBadge';
import { useTaskTypeText } from './use-task-type-text';
import { isTerminalTask, syntheticClosureText } from './task-staleness';
import { robotHoldsTask } from './task-ownership';
import { unassignedText } from './calendar/lane-layout';
import { formatDateTime } from '../../utils/date-format';
import { useDismissOnOutside } from '../../shell/use-dismiss-on-outside';
import { ShowWhereLink } from '../../shell/ShowWhereLink';
import { useTaskRequest } from '../../api/hooks/useTaskRequest';
import { isHighPriority } from '../../api/task-priority';
import './TaskDetailDrawer.css';

/**
 * "for 4 min" / "for 2 h 5 min". Minutes, never seconds: the alert that
 * pairs with this banner opens at 120 s, so anything a person reads here
 * is already minutes old, and a ticking seconds counter in a drawer that
 * re-renders on every live sample is noise.
 */
function formatAge(ms: number): string {
  const minutes = Math.max(0, Math.floor(ms / 60_000));
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  return `${hours} h ${minutes % 60} min`;
}

/**
 * The twin of `SUBMITTED_TOOLTIP` in `task-columns.tsx` — keep them in step.
 *
 * `TaskStateView.submittedAtMs` is when the PLATFORM first saw the
 * task, not when an operator pressed Dispatch. RMF's own
 * `booking.unix_millis_earliest_start_time` is a monotonic counter
 * rather than wall-clock ms (`rmf_bridge.py:850-867`), so the worker's
 * `_plausible_unix_ms()` rejects it and falls back to `_now_ms()` at
 * receive time (`rmf_bridge.py:2060-2062`). For a task dispatched from
 * this console the gap is about a second; for one RMF scheduled itself
 * it is a receive time and nothing more.
 */
const SUBMITTED_TITLE =
  'When the system first saw this task, not when someone pressed Dispatch. For a task sent from this console the two are within about a second.';

/**
 * "Priority: High" for a High task; nothing for Normal, which is every job
 * nobody chose for (2026-10-31).
 *
 * Either source says it. The dispatch request on file is what was asked
 * for -- read through the one rule, because an older api-server returns
 * the stored number as it is (an old 1-5 value is Normal). The live
 * record's own priority (worker 0.4.40, from the dispatch label or RMF's
 * booking) also covers a task the boarding retry re-sent under a new id,
 * which has no request on file. The same query as the Planned list, so
 * no second fetch.
 */
function TaskPriorityRow({ task }: { task: TaskState }) {
  const request = useTaskRequest(task.id).data?.request;
  if (!isHighPriority(task.priority) && !isHighPriority(request?.priority)) return null;
  return (
    <div className="nx-task-drawer__row" data-testid="task-priority-row">
      <span>Priority</span>
      <span>High</span>
    </div>
  );
}

function statusKind(s: TaskState['status']): 'active' | 'idle' | 'warning' | 'error' | 'completed' {
  if (s === 'queued') return 'idle';
  if (s === 'active') return 'active';
  if (s === 'completed') return 'completed';
  if (s === 'cancelled') return 'warning';
  if (s === 'failed') return 'error';
  return 'idle';
}

export function TaskDetailDrawer({
  task,
  onClose,
}: {
  task: TaskState;
  onClose: () => void;
}) {
  const panelRef = useRef<HTMLElement>(null);
  useDismissOnOutside(panelRef, onClose);

  const log = useTaskLog(task.id);
  const liftClosed = useClosedLiftScopes();
  // Null means the rules are not on, or not known: nothing here is about a lift,
  // and with them off this drawer reads as it did before them.
  const noRoute = hasNoRoute(task) && liftClosed !== null ? noRouteWords(liftClosed) : null;
  const syntheticClosure = syntheticClosureText(task);
  // The Distance and Cleaning time columns' own source: the site's live
  // store, which the stream keeps on every console and the Tasks tab's
  // catch-up read has already filled for every row it shows, this one
  // included.
  const distance = useTaskDistances(useSafeSiteId()).get(task.id);
  // A clean says which: `vacuum`, `sweep`, `dust-mop` or `scrub`.
  const typeText = useTaskTypeText(task);

  const deleteHistory = useDeleteTaskHistory();
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  /**
   * The server's `detail.code` for a failed delete, when it sent one.
   * `httpError` attaches the parsed body, so this reads the code the
   * API actually chose rather than pattern-matching its prose.
   */
  const deleteHistoryFailure = ((): string | undefined => {
    const body = (deleteHistory.error as { body?: unknown } | null)?.body;
    const detail = (body as { detail?: { code?: unknown } } | undefined)?.detail;
    return typeof detail?.code === 'string' ? detail.code : undefined;
  })();

  // The dustbag ack addresses the ROBOT, and a task record does not
  // carry a fleet -- `TaskStateView` has `assignedRobot` and nothing
  // else (`TaskSummary.assigned_fleet` is dropped by
  // `adaptTaskSummary`). RMF only guarantees a robot name is unique
  // WITHIN its fleet, so the name alone is not an address; the live
  // robot list is what turns one into a `{fleet, robot}` pair. When it
  // cannot -- the robot has not been published yet, or the task never
  // got one -- the control is replaced by a sentence saying so rather
  // than by a button that would 400.
  const robots = useRobots();
  const confirmable = isConfirmableDustbagTask(task);
  const assignedRobot = task.assignedRobot
    ? (robots.data ?? []).find((r) => r.name === task.assignedRobot)
    : undefined;

  /**
   * Delete-from-history, offered only for a task that has ENDED -- and
   * not while a robot is still running it (WS3).
   *
   * `isTerminalTask` is the same predicate the quiet badge uses:
   * `completed | failed | cancelled`, and deliberately NOT `queued` or
   * `interrupted`. But terminal is no longer the whole question: a
   * 0.4.28 worker projected RMF's `error` as `failed`, so a task the
   * fleet adapter was still driving passed it. Deleting that record got
   * it back within a second -- RMF republishes at 1 Hz and the worker
   * re-projects it -- and the operator watched the row reappear and
   * concluded the platform was broken. The api-server refuses it with
   * 409 `task_still_held_by_robot`; withholding the control means nobody
   * reaches for Delete when what they need is Cancel.
   *
   * A STALE robot state still allows it: that is the orphan case Delete
   * exists for, and `robotHoldsTask` requires freshness rather than
   * merely a matching id.
   */
  const deletable = isTerminalTask(task.status) && !robotHoldsTask(task, assignedRobot, Date.now());

  return (
    <aside ref={panelRef} className="nx-task-drawer" role="dialog" aria-label={`Task ${task.id} details`}>
      <header className="nx-task-drawer__head">
        <h2>{task.id}</h2>
        {/* A link to the Overview, pointed at this job; never a drawer. */}
        <ShowWhereLink focus={{ kind: 'task', taskId: task.id }} />
        <button type="button" aria-label="Close" onClick={onClose}>
          ×
        </button>
      </header>
      <div className="nx-task-drawer__body">
        <div className="nx-task-drawer__row">
          <span>Status</span>
          <span>
            <StatusChip
              status={statusKind(task.status)}
              /* NOT PULSING while attention stands. The pulse means
                 "healthy work in progress"; a task retrying a plan once
                 a second is in progress and NOT healthy, and one
                 animation cannot say both. */
              pulse={task.status === 'active' && !task.attention}
              label={
                noRoute?.liftBlocked
                  ? 'waiting'
                  : task.attention
                    ? `active · ${task.attention.rmfStatus}`
                    : task.status
              }
            />
            <TaskQuietBadge task={task} />
          </span>
        </div>
        {/* A synthetic terminal record is the platform's stall reaper
            closing out a task no fleet adapter owns any more. It is a
            real `failed` row and keeps its history, but nothing on the
            robot went wrong — saying "failed" alone sends an operator
            to inspect a robot that was never involved. */}
        {/* STILL RUNNING, AND SOMEBODY SHOULD LOOK. RMF's `error` means
            the fleet adapter is retrying a plan every 5 s -- not that the
            task ended. On 2026-09-15 this condition ran for 47 minutes
            with nothing on screen saying so, because the record already
            read `failed`. */}
        {noRoute && (noRoute.liftBlocked || !task.attention) && (
          <p className="nx-task-attention" data-testid="task-no-route" role="status">
            {noRoute.sentence}
            {typeof task.noRouteSinceMs === 'number' &&
              ` For ${formatAge(Date.now() - task.noRouteSinceMs)} so far.`}
          </p>
        )}
        {task.attention && !noRoute?.liftBlocked && (
          <p className="nx-task-attention" data-testid="task-attention-banner" role="status">
            {/* "The system", not "RMF", for every reader (batch B,
                2026-10-31): the drawer has no audience split, and it is
                as true for ops. */}
            Needs attention for {formatAge(Date.now() - task.attention.sinceMs)}: the system
            reports {task.attention.rmfStatus}.{task.attention.reason ? ` ${task.attention.reason}` : ''}
          </p>
        )}
        {syntheticClosure && (
          <p className="nx-task-synthetic" data-testid="task-synthetic-closure" role="note">
            {syntheticClosure}
          </p>
        )}
        {/* A genuine RMF failure reason ("robot did not localize on
            map 'L7'") is the first thing an operator wants off a
            `failed` row and used to appear nowhere in this drawer. The
            synthetic case above already speaks for itself, so don't
            say it twice. */}
        {!syntheticClosure && task.failureReason && (
          <div className="nx-task-drawer__row">
            {/* LABELLED BY THE VERDICT, not by the field name. The same
                field also carries the reason a task ended a particular
                way while still SUCCEEDING -- a dustbag run that timed
                out finishes deliberately (stranding the robot would be
                worse) but must not read like one an operator actually
                confirmed. Calling that "Failure" would be the opposite
                lie to the one this field exists to prevent. */}
            <span>{task.status === 'failed' ? 'Failure' : 'Outcome'}</span>
            <span data-testid="task-failure-reason">{task.failureReason}</span>
          </div>
        )}
        <div className="nx-task-drawer__row">
          <span>Type</span>
          <span>{typeText}</span>
        </div>
        <TaskPriorityRow task={task} />
        <div className="nx-task-drawer__row">
          <span>Robot</span>
          {/* "Not assigned yet" while the fleet has not given it one, as
              the calendar says it (2026-10-31), not a bare dash. */}
          <span data-testid="task-drawer-robot">
            {task.assignedRobot ?? unassignedText(isTerminalTask(task.status))}
          </span>
        </div>
        <div className="nx-task-drawer__row">
          <span>Triggered by</span>
          <span>{task.triggeredBy ?? '—'}</span>
        </div>
        <div className="nx-task-drawer__row">
          <span title={SUBMITTED_TITLE}>Submitted</span>
          <span data-testid="task-submitted-at">{formatDateTime(task.submittedAtMs)}</span>
        </div>
        <div className="nx-task-drawer__row">
          <span>Started</span>
          <span>{formatDateTime(task.startedAt)}</span>
        </div>
        <div className="nx-task-drawer__row">
          <span title={TASK_TIME_TOOLTIP}>Task time</span>
          <TaskElapsed task={task} />
        </div>
        <div className="nx-task-drawer__row">
          <span title={REMAINING_TOOLTIP}>Est. time remaining</span>
          <TaskRemaining task={task} />
        </div>
        <TaskDistanceRows view={distance} running={distanceStillGrowing(task.status)} />

        {/* TWO LISTS, NOT ONE. `Planned` is what was ASKED FOR, read from
            the durable dispatch intent; `Visited` is what RMF confirms
            HAPPENED, read from `task.phases`. A queued task has visited
            nothing -- its published state carries no phases at all, by
            construction -- which is why this section used to be empty for
            exactly the tasks an operator most wants to look ahead at. */}
        <section className="nx-task-drawer__section">
          <h3>Planned</h3>
          <TaskPlannedList task={task} />
        </section>

        <section className="nx-task-drawer__section">
          <h3>Visited</h3>
          <TaskPhasesList
            phases={task.phases}
            emptyText={task.status === 'queued' ? 'Not started yet.' : undefined}
          />
        </section>

        <section className="nx-task-drawer__section">
          <h3>Timeline</h3>
          {log.isLoading ? (
            <SkeletonLoader shape="text-line" count={3} />
          ) : log.isError ? (
            <p className="nx-task-drawer__error">Failed to load log: {String((log.error as Error)?.message)}</p>
          ) : (
            <TaskTimeline entries={log.data ?? []} />
          )}
        </section>

        <TaskControlActions task={task} />
        {confirmable &&
          (assignedRobot ? (
            <DustbagConfirmAction
              fleet={assignedRobot.fleet}
              robot={assignedRobot.name}
              taskId={task.id}
            />
          ) : (
            <p className="nx-task-drawer__sub" data-testid="dustbag-confirm-unaddressable">
              This task is waiting for its routine check to be confirmed, but the console cannot tell
              which fleet {task.assignedRobot ? <code>{task.assignedRobot}</code> : 'the robot'}{' '}
              belongs to, so it cannot address the confirmation. Confirm from the robot&apos;s own
              drawer on the Robots screen.
            </p>
          ))}

        {/* Erasing the record is a strictly narrower power than
            creating the work: `TASK_DELETE_HISTORY_ROLES` omits
            `foh_operator`, who may dispatch. Withheld rather than
            disabled — a control that could only ever 403 is worse
            than one that is absent — and drawn only once the task has
            ENDED, so it can never be mistaken for a way to stop a
            running one. */}
        {deletable && (
          <RoleGuard allowedRoles={[...TASK_DELETE_HISTORY_ROLES]}>
            <div className="nx-task-drawer__actions">
              <button
                type="button"
                className="nx-task-drawer__cancel"
                onClick={() => setConfirmingDelete(true)}
                disabled={deleteHistory.isPending}
                title="Erase this finished task from the console, from Metrics and from its Timeline. Cannot be undone."
                data-testid="task-delete-history-btn"
              >
                {deleteHistory.isPending ? 'Deleting…' : 'Delete from history…'}
              </button>
            </div>
          </RoleGuard>
        )}
        {deleteHistory.isError && (
          <p
            className="nx-task-drawer__error"
            data-testid="task-delete-history-error"
            role="alert"
          >
            {/* Most failures mean nothing was deleted — say so, rather
                than leaving a drawer that simply stayed open. A 409 is
                the server refusing a task that is not terminal after
                all (this record can be a stale snapshot of a task that
                resumed); a 403 means this list and the backend's
                `policy/capabilities.py` have drifted.

                `tombstone_publish_failed` is the ONE case that is not
                that, and it must not read like the others: the task IS
                gone -- rows deleted, key deleted -- and only the
                broadcast that tells other open tabs failed. Telling an
                operator "nothing was removed" there would be a lie that
                invites a retry, and the retry 404s on a task that no
                longer exists. See `api/tasks.py`'s delete handler,
                which orders the key delete before the tombstone
                precisely so this failure is the harmless one. */}
            {deleteHistoryFailure === 'task_still_held_by_robot'
              ? `${task.id} was NOT deleted: a robot is still executing it, and a deleted record would be re-projected within a second. Cancel or kill the task first.`
              : deleteHistoryFailure === 'tombstone_publish_failed'
              ? `${task.id} WAS deleted. Only the live update to other open tabs failed, so this row may linger on someone else’s screen until they refresh — do not retry.`
              : `${deleteHistory.error?.message ?? 'The task was not deleted.'} Nothing was removed.`}
          </p>
        )}
      </div>

      <ConfirmDialog
        open={confirmingDelete}
        title="Delete this task from history?"
        message={`${task.id} will be erased from the task list, from the Metrics screen’s totals, and from its own Timeline. This cannot be undone, and no export or backup of it is kept. Any support issue it raised is deliberately preserved.`}
        confirmLabel="Delete permanently"
        cancelLabel="Keep it"
        variant="danger"
        onConfirm={() => {
          setConfirmingDelete(false);
          // Re-checked here, not only on the button. The dialog can
          // sit open across a live update, and this drawer is re-used
          // in place when the task under it changes — a task that
          // resumed while the dialog was up must not be deleted by a
          // press aimed at the terminal record it used to be.
          if (!deletable) return;
          deleteHistory.mutate(task.id, {
            // Closing on success only. The row itself leaves the table
            // on its own: the server publishes a task tombstone, which
            // `fetch-sse.ts` classifies and `category-reducers.ts`
            // applies by deleting the id — nothing here touches the
            // cache by hand.
            onSuccess: () => onClose(),
          });
        }}
        onCancel={() => setConfirmingDelete(false)}
      />
    </aside>
  );
}
